/**
 * Shared booking slot rules (web BookingPage + tests).
 * Aligns with mobile BookingScreen: GET /schedule/available-slots uses `available: false` for conflicts.
 */

/** Calendar day in the user's local timezone (avoid UTC date skew from `toISOString().split('T')[0]`). */
export function formatLocalDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export interface BookingSlotInput {
  startTime: string;
  available?: boolean;
}

export function normalizeSlotsFromApi<T extends BookingSlotInput>(raw: T[]): (T & { available: boolean })[] {
  return raw.map((slot) => ({
    ...slot,
    available: slot.available ?? true,
  }));
}

export function getTimeSlotDisabledState(
  slot: BookingSlotInput,
  selectedDate: Date | null,
  now: Date = new Date()
): { disabled: boolean; title?: string } {
  if (!selectedDate) return { disabled: true };
  const isAvailable = slot.available !== false;
  const today0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const sel0 = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
  const isToday = today0.getTime() === sel0.getTime();
  const st = slot.startTime;
  const slotStart = new Date(st.includes('T') ? st : `${formatLocalDateKey(sel0)}T${st}`);
  if (Number.isNaN(slotStart.getTime())) return { disabled: true, title: 'Invalid time slot.' };
  const isPast = isToday && slotStart.getTime() <= now.getTime();
  if (isPast) {
    return { disabled: true, title: 'This time has already passed.' };
  }
  if (!isAvailable) {
    return {
      disabled: true,
      title: 'This time slot is not available because it overlaps with an existing appointment.',
    };
  }
  return { disabled: false };
}

export function isTimeSlotSelectable(
  slot: BookingSlotInput,
  selectedDate: Date | null,
  now?: Date
): boolean {
  return !getTimeSlotDisabledState(slot, selectedDate, now).disabled;
}
